import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tag, Plus, Pencil, Trash2, Mail } from "lucide-react";
import { toast } from "sonner";
import { discountText } from "@/components/DiscountCodeField";

const EMPTY = { code: "", label: "", discount_type: "percent", value: "", duration: "once", active: true, expires_at: "", max_uses: "", use_in_emails: false };
const fmtDate = (d) => d.split("-").reverse().join("/");

const CodeForm = ({ form, setForm }) => (
  <div className="space-y-3">
    <div className="space-y-1.5"><Label>Codice</Label>
      <Input value={form.code} onChange={(e) => setForm({ ...form, code: e.target.value.toUpperCase() })} placeholder="ES. BENVENUTO20" data-testid="discount-form-code" /></div>
    <div className="space-y-1.5"><Label>Dicitura (mostrata all'utente)</Label>
      <Input value={form.label} onChange={(e) => setForm({ ...form, label: e.target.value })} placeholder="Sconto di benvenuto" data-testid="discount-form-label" /></div>
    <div className="grid grid-cols-2 gap-3">
      <div className="space-y-1.5"><Label>Tipo</Label>
        <Select value={form.discount_type} onValueChange={(v) => setForm({ ...form, discount_type: v })}>
          <SelectTrigger data-testid="discount-form-type"><SelectValue /></SelectTrigger>
          <SelectContent><SelectItem value="percent">Percentuale (%)</SelectItem><SelectItem value="amount">Importo fisso (€)</SelectItem></SelectContent>
        </Select></div>
      <div className="space-y-1.5"><Label>Valore {form.discount_type === "percent" ? "(%)" : "(€)"}</Label>
        <Input type="number" min="0" step="0.01" value={form.value} onChange={(e) => setForm({ ...form, value: e.target.value })} data-testid="discount-form-value" /></div>
    </div>
    <div className="space-y-1.5"><Label>Durata</Label>
      <Select value={form.duration} onValueChange={(v) => setForm({ ...form, duration: v })}>
        <SelectTrigger data-testid="discount-form-duration"><SelectValue /></SelectTrigger>
        <SelectContent><SelectItem value="once">Solo primo pagamento</SelectItem><SelectItem value="forever">Su ogni rinnovo</SelectItem></SelectContent>
      </Select></div>
    <div className="grid grid-cols-2 gap-3">
      <div className="space-y-1.5"><Label>Scadenza (facoltativa)</Label>
        <Input type="date" value={form.expires_at || ""} onChange={(e) => setForm({ ...form, expires_at: e.target.value })} data-testid="discount-form-expires" /></div>
      <div className="space-y-1.5"><Label>Max utilizzi (facoltativo)</Label>
        <Input type="number" min="1" step="1" value={form.max_uses || ""} placeholder="Illimitati" onChange={(e) => setForm({ ...form, max_uses: e.target.value })} data-testid="discount-form-max-uses" /></div>
    </div>
    <div className="flex items-center justify-between gap-3"><Label>Usa nelle email "Passa a Premium"<span className="block text-xs text-muted-foreground font-normal">Un solo codice alla volta</span></Label>
      <Switch checked={form.use_in_emails} onCheckedChange={(v) => setForm({ ...form, use_in_emails: v })} data-testid="discount-form-use-in-emails" /></div>
    <div className="flex items-center justify-between"><Label>Attivo</Label>
      <Switch checked={form.active} onCheckedChange={(v) => setForm({ ...form, active: v })} data-testid="discount-form-active" /></div>
  </div>
);

export const DiscountCodesAdmin = ({ onChanged }) => {
  const [codes, setCodes] = useState([]);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    try { const { data } = await api.get("/admin/discount-codes"); setCodes(data); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };
  useEffect(() => { load(); }, []);

  const openForm = (c) => { setEditing(c ? c.code : "new"); setForm(c ? { ...EMPTY, ...c } : EMPTY); };

  const save = async () => {
    setSaving(true);
    try {
      const body = { ...form, value: Number(form.value), expires_at: form.expires_at || null, max_uses: form.max_uses ? Number(form.max_uses) : null };
      if (editing === "new") await api.post("/admin/discount-codes", body);
      else await api.put(`/admin/discount-codes/${encodeURIComponent(editing)}`, body);
      toast.success("Codice sconto salvato");
      setEditing(null); load(); onChanged?.();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  const remove = async (c) => {
    if (!window.confirm(`Eliminare il codice ${c.code}?`)) return;
    await api.delete(`/admin/discount-codes/${encodeURIComponent(c.code)}`);
    toast.success("Codice eliminato"); load();
  };

  return (
    <Card className="rounded-2xl" data-testid="discount-codes-admin">
      <CardHeader className="flex flex-row items-center justify-between space-y-0">
        <CardTitle className="font-heading text-lg flex items-center gap-2"><Tag className="h-5 w-5 text-primary" /> Codici sconto</CardTitle>
        <Button size="sm" onClick={() => openForm(null)} data-testid="discount-new-button"><Plus className="h-4 w-4 mr-1" /> Nuovo codice</Button>
      </CardHeader>
      <CardContent className="space-y-2">
        {codes.length === 0 && <p className="text-sm text-muted-foreground py-4 text-center">Nessun codice sconto. Creane uno.</p>}
        {codes.map((c) => (
          <div key={c.code} className="flex items-center justify-between gap-3 rounded-xl bg-secondary/50 p-3 flex-wrap" data-testid={`discount-row-${c.code}`}>
            <div className="min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                <span className="font-mono-data font-semibold">{c.code}</span>
                <Badge variant="outline" className="bg-primary/15 text-primary border-primary/30">{discountText(c)}</Badge>
                <Badge variant="outline">{c.duration === "once" ? "Primo pagamento" : "Ogni rinnovo"}</Badge>
                {c.status !== "Attivo" && <Badge variant="outline" className="text-destructive border-destructive/30" data-testid={`discount-status-${c.code}`}>{c.status}</Badge>}
                {c.use_in_emails && <Badge variant="outline" className="bg-accent/15 text-accent border-accent/30 gap-1" data-testid={`discount-email-${c.code}`}><Mail className="h-3 w-3" /> Nelle email</Badge>}
              </div>
              <div className="text-xs text-muted-foreground mt-1">{c.label || "—"} · <span data-testid={`discount-uses-${c.code}`}>{c.uses}{c.max_uses ? `/${c.max_uses}` : ""} utilizzi</span>
                {c.expires_at && <> · <span data-testid={`discount-expiry-${c.code}`}>scade il {fmtDate(c.expires_at)}</span></>}</div>
            </div>
            <div className="flex gap-1">
              <Button variant="ghost" size="icon" onClick={() => openForm(c)} data-testid={`discount-edit-${c.code}`}><Pencil className="h-4 w-4" /></Button>
              <Button variant="ghost" size="icon" onClick={() => remove(c)} data-testid={`discount-delete-${c.code}`}><Trash2 className="h-4 w-4 text-destructive" /></Button>
            </div>
          </div>
        ))}
      </CardContent>
      <Dialog open={!!editing} onOpenChange={(o) => !o && setEditing(null)}>
        <DialogContent data-testid="discount-form-dialog" aria-describedby={undefined}>
          <DialogHeader><DialogTitle>{editing === "new" ? "Nuovo codice sconto" : `Modifica ${editing}`}</DialogTitle></DialogHeader>
          <CodeForm form={form} setForm={setForm} />
          <DialogFooter><Button onClick={save} disabled={saving} data-testid="discount-form-save">{saving ? "Salvataggio…" : "Salva"}</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
};
